/**
 * =============================================================================
 * Hook: useReferralPayoutsAdmin
 * Versão: 1.0.0 (Fase 5 — Bloco 5E, item 5.11)
 *
 * Descrição:
 *   Hooks administrativos (visão TENANT) para `affiliate_payouts`.
 *   Lista solicitações de saque de TODOS os afiliados do tenant ativo e
 *   expõe mutations para aprovar (processed), marcar como paga, falhar
 *   ou cancelar.
 *
 *   - Toda leitura é restrita por `tenant_id` + RLS no banco.
 *   - Marcações de pagamento atualizam o payout E as comissões vinculadas
 *     (status `paid` quando o payout é concluído).
 *
 * IMPORTANTE: Este hook NÃO grava no módulo Financeiro do sistema. A baixa
 * financeira real (boleto/PIX) é feita externamente pelo administrador e
 * apenas espelhada aqui via campos `external_transaction_id` + `paid_at`.
 * =============================================================================
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useReferralPayoutsAdmin');

export type PayoutStatus = Database['public']['Enums']['affiliate_payout_status'];
export type PayoutMethod = Database['public']['Enums']['affiliate_payout_method'];

export interface AdminPayoutRow {
  id: string;
  amount: number;
  currency: string;
  status: PayoutStatus;
  payout_method: PayoutMethod;
  commissions_count: number;
  requested_at: string;
  processed_at: string | null;
  paid_at: string | null;
  failed_reason: string | null;
  notes: string | null;
  external_transaction_id: string | null;
  affiliate_id: string;
  affiliate: {
    id: string;
    full_name: string;
    email: string;
    pix_key: string | null;
    pix_key_type: string | null;
  } | null;
}

export interface UseReferralPayoutsFilters {
  status?: PayoutStatus | 'all';
  affiliateId?: string | 'all';
  search?: string;
}

const QK = (
  tenantId: string | null | undefined,
  filters: UseReferralPayoutsFilters,
) => ['referral-payouts-admin', tenantId, filters];

/** Lista solicitações de saque do tenant ativo.
 *
 * @deprecated A partir da v1.0.0 de `useReferralPayoutsPaginated`, prefira esse
 * hook server-side (RPC `get_referral_payouts_paginated`) para evitar SELECTs
 * diretos na tabela e o limite de 1k linhas. Este hook permanece apenas para
 * compatibilidade transitória e pode ser removido em versão futura.
 */
export function useReferralPayoutsAdmin(
  filters: UseReferralPayoutsFilters = {},
) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const normalized = useMemo<UseReferralPayoutsFilters>(
    () => ({
      status: filters.status ?? 'all',
      affiliateId: filters.affiliateId ?? 'all',
      search: filters.search?.trim() || undefined,
    }),
    [filters.status, filters.affiliateId, filters.search],
  );

  return useQuery<AdminPayoutRow[]>({
    queryKey: QK(tenantId, normalized),
    enabled: !!tenantId,
    queryFn: async () => {
      let query = supabase
        .from('affiliate_payouts')
        .select(
          `
            id,
            amount,
            currency,
            status,
            payout_method,
            commissions_count,
            requested_at,
            processed_at,
            paid_at,
            failed_reason,
            notes,
            external_transaction_id,
            affiliate_id,
            affiliate:affiliates ( id, full_name, email, pix_key, pix_key_type )
          `,
        )
        .eq('tenant_id', tenantId!)
        .order('requested_at', { ascending: false })
        .limit(500);

      if (normalized.status && normalized.status !== 'all') {
        query = query.eq('status', normalized.status);
      }
      if (normalized.affiliateId && normalized.affiliateId !== 'all') {
        query = query.eq('affiliate_id', normalized.affiliateId);
      }

      const { data, error } = await query;
      if (error) {
        log.error('Erro ao listar payouts (admin)', error);
        throw error;
      }

      const rows = (data ?? []).map((row: any) => ({
        ...row,
        affiliate: Array.isArray(row.affiliate)
          ? row.affiliate[0] ?? null
          : row.affiliate,
      })) as AdminPayoutRow[];

      const s = normalized.search?.toLowerCase();
      if (!s) return rows;
      return rows.filter((r) => {
        const aff = r.affiliate?.full_name?.toLowerCase() ?? '';
        const email = r.affiliate?.email?.toLowerCase() ?? '';
        return aff.includes(s) || email.includes(s);
      });
    },
    staleTime: 30_000,
  });
}

/** Mutations administrativas para solicitações de saque. */
export function useReferralPayoutMutations() {
  const qc = useQueryClient();
  const { currentTenant, user } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  /**
   * Invalida TODOS os caches afetados por uma mutação de payout.
   *
   * IMPORTANTE: além das listagens, invalidamos também
   * `referral-payout-details` — chave usada pelo `PayoutDetailSheet`.
   * Sem isso, o Sheet aberto continuava mostrando status antigo
   * (ex.: "Pendente" após "Marcar como falho"), porque dependia
   * apenas do realtime do Postgres, que pode não estar habilitado
   * para `affiliate_payouts`. Aceita o `id` do payout afetado para
   * disparar refetch dirigido (mais barato que invalidar todos).
   */
  const invalidate = (payoutId?: string) => {
    // Cache legado (useReferralPayoutsAdmin)
    qc.invalidateQueries({ queryKey: ['referral-payouts-admin'] });
    // Cache da nova listagem server-side (useReferralPayoutsPaginated)
    qc.invalidateQueries({ queryKey: ['referral-payouts-paginated'] });
    qc.invalidateQueries({ queryKey: ['referral-commissions-admin'] });
    qc.invalidateQueries({ queryKey: ['referral-dashboard-kpis'] });

    // Detalhes do payout aberto no Sheet lateral (PayoutDetailSheet).
    if (payoutId) {
      qc.invalidateQueries({
        queryKey: ['referral-payout-details', payoutId],
      });
    } else {
      // Fallback: invalida todas as variantes (caso o id não seja passado).
      qc.invalidateQueries({ queryKey: ['referral-payout-details'] });
    }
  };

  /**
   * Marca como em processamento.
   * Mantemos UPDATE direto pois a transição requested → processing é apenas
   * sinalização administrativa (sem efeitos colaterais em comissões).
   */
  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { error } = await supabase
        .from('affiliate_payouts')
        .update({
          status: 'processing',
          processed_at: new Date().toISOString(),
          processed_by: user?.id ?? null,
        })
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => invalidate(vars),
  });

  /**
   * Marca como pago via RPC `confirm_payout`.
   * A RPC atualiza atomicamente o payout E todas as comissões vinculadas.
   */
  const markPaidMutation = useMutation({
    mutationFn: async (params: {
      id: string;
      externalTransactionId?: string | null;
      // Campos opcionais adicionados na v1.2.0 da RPC confirm_payout
      notes?: string | null;
      paidAt?: string | null; // ISO timestamp (ex.: '2026-04-29T00:00:00Z')
    }) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { error } = await supabase.rpc('confirm_payout', {
        p_payout_id: params.id,
        p_external_transaction_id: params.externalTransactionId ?? null,
        p_notes: params.notes ?? null,
        p_tenant_hint: tenantId,
        // Cast `any` enquanto types.ts não é regenerado com o novo parâmetro
        ...(params.paidAt ? { p_paid_at: params.paidAt } as any : {}),
      } as any);
      if (error) {
        log.error('Falha ao confirmar payout', error);
        throw error;
      }
    },
    onSuccess: (_data, vars) => invalidate(vars.id),
  });

  /**
   * Marca payout como falho via RPC `fail_payout`.
   * A RPC libera atomicamente as comissões para nova tentativa.
   */
  const markFailedMutation = useMutation({
    mutationFn: async (params: { id: string; reason: string | null }) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { error } = await supabase.rpc('fail_payout', {
        p_payout_id: params.id,
        p_reason: params.reason ?? '',
        p_tenant_hint: tenantId,
      });
      if (error) {
        log.error('Falha ao marcar payout como falho', error);
        throw error;
      }
    },
    onSuccess: (_data, vars) => invalidate(vars.id),
  });

  /**
   * Cancela uma solicitação via RPC `cancel_payout`.
   * A RPC libera atomicamente as comissões vinculadas.
   */
  const cancelMutation = useMutation({
    mutationFn: async (params: { id: string; reason: string | null }) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { error } = await supabase.rpc('cancel_payout', {
        p_payout_id: params.id,
        p_reason: params.reason,
        p_tenant_hint: tenantId,
      });
      if (error) {
        log.error('Falha ao cancelar payout', error);
        throw error;
      }
    },
    onSuccess: (_data, vars) => invalidate(vars.id),
  });

  return {
    approveMutation,
    markPaidMutation,
    markFailedMutation,
    cancelMutation,
  };
}
